import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, Palette, Zap, Truck, Star, Building2 } from 'lucide-react';

const features = [
  {
    icon: Award,
    title: 'Premium Quality Materials',
    description: 'We use only the highest quality materials ensuring durability and longevity of all signage products.',
  },
  {
    icon: Palette,
    title: 'Custom Design Solutions',
    description: 'Our expert designers create bespoke signage solutions tailored to your brand identity.',
  },
  {
    icon: Zap,
    title: 'Energy Efficient LED',
    description: 'State-of-the-art LED technology that reduces energy consumption while maximizing visual impact.',
  },
  {
    icon: Truck,
    title: 'Fast Installation',
    description: 'Quick turnaround times with professional installation services throughout Gujarat.',
  },
  {
    icon: Star,
    title: '5 Star Rated Company',
    description: 'Consistently rated 5 stars by our clients on Google Reviews for exceptional service.',
  },
  {
    icon: Building2,
    title: 'Large-Scale Projects',
    description: 'Successfully delivered major projects for leading brands and government institutions.',
  },
];

const FeatureCard = ({ feature, index }) => {
  const Icon = feature.icon;
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      className="group relative"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      data-testid={`feature-card-${index}`}
    >
      <div className="flex items-start gap-5 p-4 rounded-xl hover:bg-slate-50 transition-colors duration-300">
        {/* Icon */}
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-sky-100 to-violet-100 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
          <Icon className="w-6 h-6 text-sky-500" />
        </div>

        {/* Content */}
        <div>
          <h3 className="text-lg font-['Oswald'] font-bold text-slate-800 mb-2 group-hover:text-sky-500 transition-colors duration-300">
            {feature.title}
          </h3>
          <p className="text-sm text-slate-500 font-['Manrope'] leading-relaxed">
            {feature.description}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const WhyChooseUsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="why-choose-us"
      className="py-24 md:py-32 bg-slate-50 relative overflow-hidden"
      data-testid="why-choose-us-section"
    >
      {/* Background */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-sky-100 rounded-full blur-3xl opacity-30"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-violet-100 rounded-full blur-3xl opacity-30"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Content */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sky-500 text-sm font-semibold uppercase tracking-[0.2em] mb-4 block font-['Manrope']">
              Why Choose Us
            </span>
            <h2 className="section-heading mb-6">
              THE ASCENT <span className="gradient-text">ADVANTAGE</span>
            </h2>
            <p className="text-slate-500 font-['Manrope'] leading-relaxed mb-8">
              With over 15 years of experience in the signage industry, ASCENT INTERNATIONAL INC. 
              has established itself as a leading sign board manufacturer in Ahmedabad. 
              Our commitment to quality, innovation, and customer satisfaction sets us apart.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 p-6 bg-white rounded-2xl shadow-soft border border-gray-100">
              {[
                { number: '500+', label: 'Projects' },
                { number: '15+', label: 'Years' },
                { number: '100%', label: 'Quality' },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="stat-number text-2xl md:text-3xl font-['Space_Grotesk']">
                    {stat.number}
                  </div>
                  <div className="text-xs text-slate-500 font-['Manrope'] mt-1">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Features */}
          <div className="space-y-4">
            {features.map((feature, index) => (
              <FeatureCard key={index} feature={feature} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
